import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useAemConfig } from '@as-react/common-components';
import { basketMutations, checkoutQueries } from '@as-react/common-queries';
import { ABTestCookie, MainWebShop, PaymentGroupName } from '@as-react/common-types';
import { isBasketNotFound, isBasketNotInitialized, isCheckoutStepInvalid } from '@as-react/common-utils';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Divider, StackBlock, TextAlpha } from '@yonderland/andes-react';
import { isAxiosError } from 'axios';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { PayByVoucher } from '../../components/checkout/payment/PayByVoucher';
import { PaymentGroups } from '../../components/checkout/payment/PaymentGroups';
import { PaymentNotification } from '../../components/checkout/payment/PaymentNotification';
import { PaymentVoucherBlock } from '../../components/checkout/payment/PaymentVoucherBlock';
import { ShoplandingPaymentGroups } from '../../components/checkout/payment/ShoplandingPaymentGroups';
import { I18N_NAMESPACES } from '../../constants/i18nConstants';
import { CHOSEN_PAYMENT_OPTION } from '../../constants/StorageKeysConstants';
import { usePaymentState } from '../../context/CheckoutPaymentContext';
import { CheckoutType } from '../../enums/AnalyticsEnums';
import { useABTest } from '../../hooks/useABTest';
import { useOAuth } from '../../hooks/useOAuth';
import { analyticsActions } from '../../redux/analytics/actions/AnalyticsActions.creators';
import { basketActions } from '../../redux/basket/actions/BasketActions.creators';
import ApplePayUtil from '../../util/ApplePayUtil';
import LocalStorageUtil from '../../util/LocalStorageUtil';
export const AMEX_METHOD = 'AMEX';
const removeAmexPaymentOption = (groups) => {
    const creditCardGroup = groups.find(group => group.name === PaymentGroupName.CREDITCARD);
    if (creditCardGroup) {
        creditCardGroup.paymentOptions = creditCardGroup === null || creditCardGroup === void 0 ? void 0 : creditCardGroup.paymentOptions.filter(option => option.name !== AMEX_METHOD);
    }
    return groups;
};
export const CheckoutPaymentContainer = () => {
    const [isSavePointCreated, setIsSavePointCreated] = useState(false);
    const { t } = useTranslation(I18N_NAMESPACES.buy);
    const { defaultRequestParams, servicesEndpoint, mainWebShop, root, isShopLanding } = useAemConfig();
    const { hasBCookie: disableAmexPaymentCookie } = useABTest(ABTestCookie.DISABLE_AMEX_PAYMENT);
    const isCotswold = mainWebShop === MainWebShop.COTSWOLD;
    const [, dispatch] = usePaymentState();
    const { isLoggedIn } = useOAuth();
    const reduxDispatch = useDispatch();
    const { getPaymentInfo } = checkoutQueries({
        servicesEndpoint,
        defaultRequestParams,
    });
    const { postSavePoint } = basketMutations({
        servicesEndpoint,
        defaultRequestParams,
    });
    const { mutate: mutatePostSavePoint } = useMutation(postSavePoint);
    const { data: paymentInfo, error: paymentInfoError } = useQuery(getPaymentInfo({
        select: data => {
            if (!data.paymentGroups) {
                return data;
            }
            let groups = data.paymentGroups.filter(group => [PaymentGroupName.APPLEPAY, PaymentGroupName.APPLEPAY_SAFERPAY].includes(group.name)
                ? ApplePayUtil.canMakePayments()
                : true);
            if (disableAmexPaymentCookie) {
                groups = removeAmexPaymentOption(groups);
            }
            data.paymentGroups = groups;
            return Object.assign(Object.assign({}, data), { paymentGroups: groups });
        },
    }));
    if (paymentInfoError && isAxiosError(paymentInfoError)) {
        if (isBasketNotFound(paymentInfoError) ||
            isBasketNotInitialized(paymentInfoError) ||
            isCheckoutStepInvalid(paymentInfoError)) {
            location.href = `${root}checkout/basket-overview.html`;
        }
    }
    useEffect(() => {
        reduxDispatch(basketActions.createGetAction());
        reduxDispatch(analyticsActions.checkoutType(isLoggedIn ? CheckoutType.LOGGED_IN : CheckoutType.GUEST));
    }, []);
    useEffect(() => {
        if (!paymentInfo) {
            return;
        }
        const paymentData = Object.assign({}, paymentInfo);
        if (!isSavePointCreated && paymentInfo && !paymentInfo.chosenPaymentOptionId) {
            mutatePostSavePoint(undefined, {
                onSuccess() {
                    setIsSavePointCreated(true);
                },
            });
        }
        if (isCotswold &&
            (paymentInfo === null || paymentInfo === void 0 ? void 0 : paymentInfo.paymentGroups) &&
            !paymentInfo.chosenPaymentOptionId &&
            !paymentInfo.chosenPaymentOption &&
            !LocalStorageUtil.getJsonItem(CHOSEN_PAYMENT_OPTION)) {
            const creditCardGroup = paymentInfo.paymentGroups.find(group => group.name === PaymentGroupName.CREDITCARD);
            if (creditCardGroup && creditCardGroup.paymentOptions[0]) {
                paymentData.chosenPaymentOptionId = creditCardGroup.paymentOptions[0].paymentOptionId;
                paymentData.chosenPaymentOption = creditCardGroup.name;
            }
        }
        else if ((paymentInfo === null || paymentInfo === void 0 ? void 0 : paymentInfo.paymentGroups) && !paymentInfo.chosenPaymentOptionId && !paymentInfo.chosenPaymentOption) {
            const storageGroupName = LocalStorageUtil.getJsonItem(CHOSEN_PAYMENT_OPTION);
            if (storageGroupName) {
                const group = paymentInfo.paymentGroups.find(group => group.name.replace(/\s+/g, '') === storageGroupName);
                if (group && group.paymentOptions[0]) {
                    paymentData.chosenPaymentOptionId = group.paymentOptions[0].paymentOptionId;
                    paymentData.chosenPaymentOption = group.name;
                }
            }
        }
        dispatch({ type: 'set_payment_info', payload: paymentData });
    }, [paymentInfo]);
    if (!paymentInfo) {
        return null;
    }
    return (_jsxs(StackBlock, { gap: "6", children: [_jsxs(StackBlock, { gap: "3", children: [_jsx(TextAlpha, { variant: "headingS", children: t(`${I18N_NAMESPACES.buy}:buy.progress.payment`) }), _jsx(TextAlpha, { children: t(`${I18N_NAMESPACES.buy}:buy.payment.choose.payment.option`) }), _jsx(PaymentNotification, {})] }), isShopLanding ? (_jsx(ShoplandingPaymentGroups, {})) : (_jsxs(_Fragment, { children: [paymentInfo.paymentGroups && (_jsxs(StackBlock, { gap: "6", children: [isCotswold ? _jsx(PaymentGroups, {}) : _jsx(PaymentVoucherBlock, {}), _jsx(Divider, {}), isCotswold ? _jsx(PaymentVoucherBlock, {}) : _jsx(PaymentGroups, {})] })), paymentInfo.fullyPaid && _jsx(PayByVoucher, {})] }))] }));
};
